import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { Facebook, Instagram } from "lucide-react";
import { useT } from "../hooks/useT";
import { MUNICIPALITIES } from "../data/municipalities";
import { DESTINATIONS } from "../config/destinations";

const TOP_MUNIS = MUNICIPALITIES.slice(0, 12);
const TOP_DESTS = Object.values(DESTINATIONS).slice(0, 12);
const PENINSULA_ANCHORS = { "/fibra-movil": "fibra-movil", "/tarifas-movil": "tarifas-movil", "/fibra-fijo": "fibra-fijo", "/empresas": "empresas", "/tv": "livewifi-tv" };

const Footer = ({ onLegalPage }) => {
  const t = useT();
  const isPeninsula = useLocation().pathname === "/peninsula";
  const ProductLink = ({ to, testId, children }) =>
    isPeninsula && PENINSULA_ANCHORS[to] ? (
      <a href={`#${PENINSULA_ANCHORS[to]}`} data-testid={testId} className="text-white/60 hover:text-[#7DD500]">{children}</a>
    ) : (
      <Link to={to} data-testid={testId} className="text-white/60 hover:text-[#7DD500]">{children}</Link>
    );
  return (
  <footer className="py-12 bg-[#050505] border-t border-white/10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-5 gap-8 mb-12">
        <div className="md:col-span-2">
          <Logo className="h-10 mb-4" />
          <p className="text-white/60 text-sm">{t("footer_about")}</p>
        </div>
        <div>
          <h4 className="font-bold text-white mb-4 text-sm">{t("footer_products").toUpperCase()}</h4>
          <ul className="space-y-2 text-sm">
            <li><ProductLink to="/fibra-movil">{t("nav_fibra_movil")}</ProductLink></li>
            <li><ProductLink to="/tarifas-movil">{t("nav_tarifas_movil")}</ProductLink></li>
            <li><ProductLink to="/fibra-fijo">{t("nav_fibra_fijo")}</ProductLink></li>
            <li><ProductLink to="/empresas">{t("nav_empresas")}</ProductLink></li>
            <li><ProductLink to="/tv">LiveWifi TV</ProductLink></li>
            <li>
              <Link to="/travel" className="text-white/60 hover:text-[#7DD500] inline-flex items-center gap-1.5" data-testid="footer-travel-link">
                Travel eSIM
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#7DD500]/15 text-[#7DD500] uppercase tracking-wider">{t("section_new")}</span>
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="font-bold text-white mb-4 text-sm">{t("footer_company").toUpperCase()}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/roaming" className="text-white/60 hover:text-[#7DD500]">{t("nav_roaming")}</Link></li>
            <li><a href="https://clienteslivewifi.ispgestion.com/site/login" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#7DD500]">{t("nav_clients_area")}</a></li>
            <li><Link to="/contacto" className="text-white/60 hover:text-[#7DD500]">{t("footer_contact")}</Link></li>
            <li><Link to="/blog" data-testid="footer-blog-link" className="text-white/60 hover:text-[#7DD500]">Blog</Link></li>
          </ul>
          <h4 className="font-bold text-white mb-3 mt-5 text-sm">FIBRA POR ISLA</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/fibra-gran-canaria" data-testid="footer-isla-gran-canaria" className="text-white/60 hover:text-[#7DD500]">Gran Canaria</Link></li>
            <li><Link to="/fibra-tenerife" data-testid="footer-isla-tenerife" className="text-white/60 hover:text-[#7DD500]">Tenerife</Link></li>
            <li><Link to="/fibra-lanzarote" data-testid="footer-isla-lanzarote" className="text-white/60 hover:text-[#7DD500]">Lanzarote</Link></li>
            <li><Link to="/fibra-fuerteventura" data-testid="footer-isla-fuerteventura" className="text-white/60 hover:text-[#7DD500]">Fuerteventura</Link></li>
            <li><Link to="/fibra-la-palma" data-testid="footer-isla-la-palma" className="text-white/60 hover:text-[#7DD500]">La Palma</Link></li>
            <li><Link to="/fibra-la-gomera" data-testid="footer-isla-la-gomera" className="text-white/60 hover:text-[#7DD500]">La Gomera</Link></li>
            <li><Link to="/fibra-el-hierro" data-testid="footer-isla-el-hierro" className="text-white/60 hover:text-[#7DD500]">El Hierro</Link></li>
          </ul>
          <h4 className="font-bold text-white mb-3 mt-5 text-sm">APP</h4>
          <ul className="space-y-2 text-sm">
            <li>
              <a href="https://play.google.com/store/apps/details?id=com.ispgestionclientes.livewifi" target="_blank" rel="noopener noreferrer" data-testid="footer-app-android" className="text-white/60 hover:text-[#7DD500] flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M3.18 23.76c-.34-.17-.55-.52-.55-.93V1.17c0-.41.21-.76.55-.93l11.02 11.76L3.18 23.76zM15.55 13.32l2.58 2.58-10.4 5.93 7.82-8.51zM19.85 10.67l-2.58 1.47-2.42-2.14 2.42-2.14 2.58 1.47c.73.42.73 1.1 0 1.34zM7.73 2.87l10.4 5.93-2.58 2.58L7.73 2.87z"/></svg>
                Google Play
              </a>
            </li>
            <li>
              <a href="https://apps.apple.com/app/mi-livewifi/id6472044867" target="_blank" rel="noopener noreferrer" data-testid="footer-app-ios" className="text-white/60 hover:text-[#7DD500] flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                App Store
              </a>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="font-bold text-white mb-4 text-sm">{t("footer_contact").toUpperCase()}</h4>
          <ul className="space-y-2 text-sm">
            <li><a href="tel:+34828182100" className="text-white/60 hover:text-[#7DD500]">828 182 100</a></li>
            <li><a href="tel:+34601651014" className="text-white/60 hover:text-[#7DD500]">601 651 014</a></li>
            <li><a href="mailto:info@livewifi.es" className="text-white/60 hover:text-[#7DD500]">info@livewifi.es</a></li>
          </ul>
          <div className="flex items-center gap-3 mt-4">
            <a href="https://www.facebook.com/Suconexion5G" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-[#7DD500] transition-colors"><Facebook className="w-4 h-4" /></a>
            <a href="https://www.instagram.com/livewifi_es/" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-[#7DD500] transition-colors"><Instagram className="w-4 h-4" /></a>
            <a href="https://www.tiktok.com/@livewifi_es" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-[#7DD500] transition-colors"><svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.37a8.16 8.16 0 004.76 1.52v-3.4a4.85 4.85 0 01-1-.8z"/></svg></a>
          </div>
        </div>
      </div>

      {/* SEO internal links: fibra por municipio + eSIM por destino */}
      <div className="pt-6 border-t border-white/10 mb-6 grid md:grid-cols-2 gap-6" data-testid="footer-seo-links">
        <div>
          <h4 className="font-bold text-white mb-3 text-xs">FIBRA POR MUNICIPIO</h4>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
            {TOP_MUNIS.map((m) => (
              <Link key={m.slug} to={`/fibra-canarias/${m.slug}`} data-testid={`footer-muni-${m.slug}`}
                className="text-white/40 hover:text-[#7DD500] transition-colors">
                {m.nombre}
              </Link>
            ))}
            <Link to="/cobertura" className="text-[#7DD500]/70 hover:text-[#7DD500] transition-colors font-bold">Ver cobertura →</Link>
          </div>
        </div>
        <div>
          <h4 className="font-bold text-white mb-3 text-xs">eSIM POR DESTINO</h4>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
            {TOP_DESTS.map((d) => (
              <Link key={d.slug} to={`/travel/${d.slug}`} data-testid={`footer-dest-${d.slug}`}
                className="text-white/40 hover:text-[#7DD500] transition-colors">
                eSIM {d.name?.es || d.h1?.es?.[1] || d.slug}
              </Link>
            ))}
            <Link to="/travel" className="text-[#7DD500]/70 hover:text-[#7DD500] transition-colors font-bold">Todos los destinos →</Link>
          </div>
        </div>
      </div>

      <div className="pt-6 border-t border-white/10 mb-6">
        <h4 className="font-bold text-white mb-3 text-xs">{t("footer_legal").toUpperCase()}</h4>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
          <button onClick={() => onLegalPage("condiciones-contratacion")} data-testid="footer-legal-condiciones" className="text-white/40 hover:text-[#7DD500] transition-colors">{t("footer_terms")}</button>
          <button onClick={() => onLegalPage("aviso-legal")} data-testid="footer-legal-aviso" className="text-white/40 hover:text-[#7DD500] transition-colors">{t("footer_legal")}</button>
          <button onClick={() => onLegalPage("privacidad")} data-testid="footer-legal-privacidad" className="text-white/40 hover:text-[#7DD500] transition-colors">{t("footer_privacy")}</button>
          <button onClick={() => onLegalPage("proteccion-datos")} data-testid="footer-legal-datos" className="text-white/40 hover:text-[#7DD500] transition-colors">{t("footer_data_protection")}</button>
          <button onClick={() => onLegalPage("cookies")} data-testid="footer-legal-cookies" className="text-white/40 hover:text-[#7DD500] transition-colors">{t("footer_cookies")}</button>
          <button onClick={() => onLegalPage("roaming")} data-testid="footer-legal-roaming" className="text-white/40 hover:text-[#7DD500] transition-colors">{t("nav_roaming")}</button>
          <button onClick={() => onLegalPage("velocidades")} data-testid="footer-legal-velocidades" className="text-white/40 hover:text-[#7DD500] transition-colors">{t("footer_speed_legal")}</button>
        </div>
      </div>

      <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-white/40 text-xs">© {new Date().getFullYear()} LiveWifi. {t("footer_rights")}.</p>
        <p className="text-white/40 text-xs" data-testid="footer-canarias-disclaimer">
          {t(isPeninsula ? "footer_peninsula_prices" : "footer_canarias_prices")}
        </p>
      </div>
    </div>
  </footer>
  );
};

export default Footer;
